import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { startPremiumCheckout } from "../lib/stripe";
import Button from "./Button";
import { Crown } from "lucide-react";
import FeedbackToast from "./FeedbackToast";

interface PremiumCTAProps {
  email: string;
  archetype: string;
  reportId: string;
}

const ctaVariations = [
  {
    headline: "Desbloquea tu Reporte Premium Completo de Arquetipo Latino™",
    body: "Descubre los mejores trabajos para ti, qué roles evitar, una hoja de ruta personalizada para tu crecimiento profesional, prompts de IA, consejos de carrera y una afirmación poderosa—todo adaptado a tu arquetipo del Test Latino™.",
    button: (
      <>
        {" "}
        Desbloquear mi Reporte Premium Completo -{" "}
        <span style={{ textDecoration: "line-through" }}>$15</span> $7
      </>
    ),
  },
  {
    headline: "Your Archetype Report Is Only 30% Complete",
    body: "You've seen the summary. Now unlock:\n- Top 5 career role matches\n- Archetype-specific AI prompts\n- Personalized growth plan & affirmation\n- Printable career roadmap\nAll for just $7.",
    button: "Get My Full Report Now",
  },
  {
    headline: "Over 2,000 Professionals Have Unlocked Their Premium Report",
    body: "Make your results actionable. This is your moment to level up with a full report designed by Jose Piñero—the Top Coach for Latinos.\nThis week only: access it all for just $7.",
    button: "Yes, I Want the Premium Version",
  },
];

const PremiumCTA: React.FC<PremiumCTAProps> = ({
  email,
  archetype,
  reportId,
}) => {
  const [selectedVariant, setSelectedVariant] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trackGAEvent = (eventName: string, eventParams?: Record<string, any>) => {
    console.log("Tracking GA Event:", eventName, eventParams);
    if (window.gtag) {
      console.log("Call GA Event");
      window.gtag('event', eventName, eventParams || {});
    }
  };

  useEffect(() => {
    const getActiveVariant = async () => {
      try {
        const { data, error: variantError } = await supabase
          .from("app_settings")
          .select("active_cta_variant")
          .eq("key", "cta_config")
          .single();

        if (!variantError && data) {
          setSelectedVariant(data.active_cta_variant);
        } else {
          setSelectedVariant(Math.floor(Math.random() * ctaVariations.length));
        }

        // Log the variant view
        await supabase.from("cta_variant_logs").insert([
          {
            email,
            archetype,
            variant_index: selectedVariant,
            viewed_at: new Date().toISOString(),
          },
        ]);
      } catch (err) {
        console.error("Error setting CTA variant:", err);
        setError("Failed to load premium offer. Please try again.");
      }
    };

    getActiveVariant();
  }, [email, archetype, selectedVariant]);

  const handlePurchase = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!email) {
        throw new Error("Email is required for checkout");
      }

      trackGAEvent("purchase_premium_cta", {
        email,
        archetype,
        reportId,
      });
      
      await startPremiumCheckout(email, archetype, reportId);
    } catch (err) {
      console.error("Checkout error:", err);
      setError("Failed to start checkout. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const cta = ctaVariations[selectedVariant];

  return (
    <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-lg shadow-lg text-center">
      <div className="flex justify-center mb-4">
        <div className="bg-orange-500 p-3 rounded-full">
          <Crown className="h-6 w-6 text-white" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-800 mb-4">{cta.headline}</h2>

      <div className="text-gray-600 mb-6 whitespace-pre-line">{cta.body}</div>

      <Button
        onClick={handlePurchase}
        disabled={isLoading}
        id="premium-cta-button"
        fullWidth
        size="large"
      >
        {isLoading ? "Procesando..." : cta.button}
      </Button>

      <div className="text-gray-600 mt-4 mb-6 whitespace-pre-line">
        Si compras ahora, puedes obtenerlo por <b>$7</b> en lugar del precio regular de <b>$15</b>
      </div>

      {error && (
        <FeedbackToast
          message={error}
          type="error"
          onClose={() => setError(null)}
        />
      )}

      <p className="text-sm text-gray-500 mt-4">
        Con la confianza de más de 2,000 profesionales • Garantía de satisfacción 100%
      </p>
    </div>
  );
};

export default PremiumCTA;
